import React, { useRef, useState, useEffect, FC, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import cx from 'classnames';
import Link from 'next/link';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
// import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import DocumentFile from 'components/Icons/DocumentFile';
import TrashOutlined from 'components/Icons/TrashOutlined';
import PdRangeTooltipMessage from 'components/Prescription/PdRangeTooltipMessage';
import Spinner from 'components/Spinner/Spinner';
import { PD_RANGE_BY_FRAME_SIZE } from 'constants/pdRange';
import prescriptionService from 'services/prescription.service';
import { notifications } from '../../constants/notificationMessages';
import {
  CREATE_PD,
  DELETE_PRESCRIPTION
} from '../../gqlStrings/mutations/Prescription';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import {
  setPdSelectPId,
  setPdSelectPrescriptionId,
  setPrescriptions,
  setSelectedPrescription,
  setUsePrescriptionButton,
  setSelectBoxPrescription,
  setPds
} from '../../redux/features/prescription/prescriptionSlice';
import { RootState } from '../../redux/store';
import { format } from "date-fns";
// import { setSelectedPrescription } from '../../redux/features/prescription/prescriptionSlice';
import { AddPdHandler, PDSelectDropdownWithAdd } from './PDSelectDropdown';
import { UIRadioCheck } from './UIRadio';
import UITooltip from './UITooltip';
import { showWarningMessage } from 'utils/toast.util';
import { getProperPrescriptionDetail } from 'functions/prescriptionDetail';

const PrescriptionBox: FC<{
  setEditPrescription?: React.Dispatch<React.SetStateAction<any>>;
  setPrescriptionEditForm?: React.Dispatch<React.SetStateAction<any>>;
  setHome?: React.Dispatch<React.SetStateAction<any>>;
  profile?: boolean;
  // eslint-disable-next-line no-unused-vars
  prescription?: any;
  resetPrescription?: Function;
  // eslint-disable-next-line no-unused-vars
  onSubmit?: (payload: any) => void;
  selectDefault?: boolean;
}> = ({ prescription, profile, onSubmit, selectDefault }) => {
  const [editOpen, setEditOpen] = useState(false);

  const MySwal = withReactContent(Swal);

  const dropRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(dropRef, handleClickOutside);
  // const dispatch = useDispatch();
  function handleClickOutside() {
    setEditOpen(false);
  }
  const dispatch = useDispatch();

  const pds = useSelector((state: RootState) => state.prescriptionEvent.pds);

  const [isPrescriptionUpdateLoading, setIsPrescriptionUpdateLoading] =
    useState(false);

  const submitHandler = async () => {
    setIsPrescriptionUpdateLoading(true);
    await prescriptionService.updatePdById({
      pdId: prescriptionPdMap[prescription.id],
      prescriptionId: prescription.id
    });
    const selectedPd = pds.find(
      (pd: any) => pd.id === prescriptionPdMap[prescription.id]
    );
    setSelectPd(selectedPd);
    setIsPrescriptionUpdateLoading(false);
    onSubmit?.({ ...prescription, pd: { ...selectedPd } });
  };

  const [isCreatePDFormVisible, setIsCreatePDFormVisible] = useState(false);

  const prescriptions = useSelector(
    (state: RootState) => state.prescriptionEvent.prescriptions
  );

  const [prescriptionPdMap, setPrescriptionPdMap] = useState<
    Record<string, string>
  >({});

  const selectedPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectedPrescription
  );

  const selectBoxPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectBoxPrescription
  );

  const usePrescriptionButton = useSelector(
    (state: RootState) => state.prescriptionEvent.usePrescriptionButton
  );

  const frameSize = useSelector(
    (state: RootState) => state.prescriptionEvent.frameSize
  );

  const selectedPdId = useMemo(
    () => prescriptionPdMap[prescription.id],
    [prescription.id, prescriptionPdMap]
  );

  const bagItems = useSelector((state: RootState) => state.bagEvent.bag);

  const pdOptions = useMemo(() => {
    return pds
      .map((pd: any) => {
        const isMonocular =
          pd?.right_monocular_dist_pd || pd?.left_monocular_dist_pd;

        return isMonocular
          ? {
            id: pd.id,
            pd: pd.binocular_dist_pd,
            rightHeight: pd.right_height_multifocal,
            leftHeight: pd.left_height_multifocal,
            left: pd.left_monocular_dist_pd,
            right: pd.right_monocular_dist_pd
          }
          : {
            id: pd.id,
            pd: pd.binocular_dist_pd
          };
      })
      .filter((userPd: any) => {
        const isMonocular = userPd?.right || userPd?.left;
        if (!frameSize) return true;

        const pdRange = PD_RANGE_BY_FRAME_SIZE[frameSize];

        if (!isMonocular)
          return (
            Number(userPd.pd) >= Number(pdRange.min) &&
            Number(userPd.pd) <= Number(pdRange.max)
          );
        return (
          Number(userPd.left) >= pdRange.min / 2 &&
          Number(userPd.left) <= pdRange.max / 2 &&
          Number(userPd.right) >= pdRange.min / 2 &&
          Number(userPd.right) <= pdRange.max / 2
        );
      });
  }, [frameSize, pds]);

  const isUsePrescriptionButtonEnabled = useMemo(() => {
    return (
      !!prescriptionPdMap[prescription.id] &&
      !!pds.find((pd: any) => pd.id === prescriptionPdMap[prescription.id])
    );
  }, [pds, prescription.id, prescriptionPdMap]);

  function setSelectPd(pd: any) {
    // dispatch(setPdSelectPrescriptionId(null));
    dispatch(setUsePrescriptionButton(true));
    dispatch(setSelectBoxPrescription({ ...prescription, pd: { ...pd } }));
  }

  useEffect(() => {
    setIsCreatePDFormVisible(false);
    const pdMap = prescriptions.reduce((acc: any, current: any) => {
      const isValid = !!pdOptions.find(
        (userPd: any) => userPd.id === current?.pd?.id
      );
      acc[current.id] = isValid ? current?.pd?.id : null;
      return acc;
    }, {});
    setPrescriptionPdMap(pdMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectBoxPrescription]);

  const updateSelectedPd = (pd: any) => {
    if (!pd.id) return;
    setPrescriptionPdMap({
      ...prescriptionPdMap,
      [prescription.id]: pd.id
    });
  };

  const [mutateDeletePrescriptionFunction] = useMutation(DELETE_PRESCRIPTION, {
    context: { clientName: 'laravel' },
    onCompleted: () => {
      toast.custom((t) => (
        <div
          onClick={() => toast.dismiss(t.id)}
          className={`${t.visible ? 'animate-enter' : 'animate-leave'
            } flex w-[300px] cursor-default select-none items-center justify-center rounded bg-brand-green p-4 text-center text-sm font-medium text-white shadow-lg`}>
          {notifications.DELETE_PRESCRIPTION_MESSAGE}
        </div>
      ));
    },
    onError: (error) => {
      console.log(error);
    }
  });

  const checkInBasktet = (prescriptionId: number): boolean => {
    var prescriptionIdArray: any = [];
    [...bagItems.items]
      .filter((it: any) => it[0].type === 'extradata')
      .map((glasses: any) => {
        glasses[0].extraData.map((extraData: any) => {
          if (extraData.key == 'prescription') {
            var prescriptionValue;
            try {
              prescriptionValue = JSON.parse(extraData.value);
            } catch (e) {
              prescriptionValue = extraData.value;
            }
            prescriptionIdArray.push(prescriptionValue.id);
          }
        });
      });

    return prescriptionIdArray.includes(prescriptionId);
  };

  function deleteModal() {
    if (checkInBasktet(prescription.id)) {
      showWarningMessage(notifications.PRESCRIPTION_USED_DETELED, false);
    } else {
      MySwal.fire({
        width: 380,
        html: (
          <div>
            <p className="text-base font-medium">
              Are you sure want to remove this prescription?
            </p>
            <div className="mt-4 flex w-full items-center justify-center">
              <button
                className="mr-3 rounded bg-brand-black50 px-5 py-2 text-sm text-white"
                onClick={() => {
                  MySwal.clickConfirm();
                }}>
                Yes, delete
              </button>
              <button
                className="rounded border border-brand-grey175 bg-white px-5 py-2 text-sm text-brand-black50"
                onClick={() => MySwal.close()}>
                Cancel
              </button>
            </div>
          </div>
        ),
        showConfirmButton: false
      }).then((result) => {
        if (result.isConfirmed) {
          mutateDeletePrescriptionFunction({
            variables: {
              input: {
                id: prescription.id
              }
            }
          });

          if (
            selectedPrescription &&
            prescription.id === selectedPrescription.id
          ) {
            dispatch(setSelectedPrescription(null));
            dispatch(setSelectBoxPrescription(null));
            dispatch(setUsePrescriptionButton(false));
          }
          const updatedItems = prescriptions.filter(
            (el: { id: any }) => el.id !== prescription.id
          );
          dispatch(setPrescriptions(updatedItems));
        }
      });
    }
  }

  const getNearText = (prescription: any) => {
    const nearValue = prescription.add_near;
    if (nearValue == '0' || nearValue == null) {
      return '';
    }

    return 'Near Add +' + nearValue;
  };

  const getIntText = (prescription: any) => {
    const nearValue = prescription.add_int;
    if (nearValue == '0' || nearValue == null) {
      return '';
    }

    return 'Int Add +' + nearValue;
  };

  useEffect(() => {
    if (selectedPrescription) {
      dispatch(setSelectBoxPrescription(selectedPrescription));
    }
  }, [dispatch, selectedPrescription]);

  useEffect(() => {
    if (selectDefault) {
      selectPrescription(prescription);
    }
    if (selectBoxPrescription?.id === prescription?.id && prescription.pd) {
      dispatch(setUsePrescriptionButton(true));
    }
  });

  const selectPrescription = (prescription: any) => {
    if (profile || selectBoxPrescription?.id === prescription?.id) return;

    if (prescription.pd) {
      dispatch(setSelectBoxPrescription(prescription));
      dispatch(setPdSelectPrescriptionId(null));
      dispatch(setPdSelectPId(null));
    } else {
      dispatch(setSelectBoxPrescription(prescription));
      dispatch(setPdSelectPrescriptionId(prescription.id));
    }
  };

  useEffect(() => {
    if (selectBoxPrescription?.id === prescription?.id && prescription.pd) {
      dispatch(setUsePrescriptionButton(true));
    }
  });

  const prescriptionSelected = useMemo(() => {
    if (profile && selectBoxPrescription) return;

    return selectBoxPrescription?.id === prescription?.id;
  }, [prescription?.id, profile, selectBoxPrescription]);

  return (
    <div className="relative mt-5 w-full">
      <div
        className={`relative flex rounded border border-transparent bg-brand-grey650 p-[18px] ${!profile ? 'cursor-pointer' : ''
          } ${prescriptionSelected ? 'border-brand-grey500' : ''}`}
        onClick={() => selectPrescription(prescription)}>
        {!profile && (
          <div className="mr-[10px] mt-[4px] hidden sm:block">
            <UIRadioCheck
              selected={prescription.id === selectBoxPrescription?.id}
            />
          </div>
        )}

        <div className="w-full sm:w-[calc(100%-40px)]">
          <div className="relative mb-[20px] flex flex-col justify-between pr-[45px] sm:flex-row">
            {!profile && (
              <div className="mb-[12px] block sm:hidden">
                <UIRadioCheck
                  selected={prescription.id === selectBoxPrescription?.id}
                />
              </div>
            )}
            <h5 className="font-sans text-base font-medium text-brand-black100">
              Prescription
            </h5>
            <div className="flex items-center font-sans text-sm text-[12px] font-normal text-brand-grey450 mdx:flex-wrap">
              <span className="pr-1 text-brand-grey75">Created on</span>
              {format(new Date(prescription.created_at.split(' ')[0]), 'dd MMMM yyyy')}
            </div>
          </div>

          {prescription.contactLensPrescription && (
            <div>
              <div className="mb-3 font-sans text-sm text-[14px] text-brand-black75">
                <p> {getProperPrescriptionDetail(prescription.standardised_prescription_display_array.right)} </p>
                <p> <span className="font-medium">DIA</span> {Number(prescription.contactLensPrescription.right_diameter).toFixed(2)} </p>
                <p> <span className="font-medium">BC</span> {Number(prescription.contactLensPrescription.right_base_curve).toFixed(2)} </p>
                {prescription.contactLensPrescription.right_near_add && (<p><span className="font-medium">Add Near</span> {Number(prescription.contactLensPrescription.right_near_add).toFixed(2)}</p>)}
              </div>
              <div className="mb-3 font-sans text-sm text-[14px] text-brand-black75">
                <p> {getProperPrescriptionDetail(prescription.standardised_prescription_display_array.left)} </p>
                <p> <span className="font-medium">DIA</span> {Number(prescription.contactLensPrescription.left_diameter).toFixed(2)} </p>
                <p> <span className="font-medium">BC</span> {Number(prescription.contactLensPrescription.left_base_curve).toFixed(2)} </p>
                {prescription.contactLensPrescription.left_near_add && (<p><span className="font-medium">Add Near</span> {Number(prescription.contactLensPrescription.left_near_add).toFixed(2)}</p>)}
              </div>

            </div>
          )}

          <div className="mb-[-3px]">
            {(prescription.standardised_prescription_display_array.left !==
              'L Plano DS' ||
              prescription.standardised_prescription_display_array.right !==
              'R Plano DS') && (!prescription.contactLensPrescription) && (
                <>
                  <div className="mb-3 font-sans text-sm text-[14px] text-brand-black75">
                    {getProperPrescriptionDetail(prescription.standardised_prescription_display_array.right)}
                  </div>
                  <div className="mb-3 font-sans text-sm text-[14px] text-brand-black75">
                    {getProperPrescriptionDetail(prescription.standardised_prescription_display_array.left)}
                  </div>
                </>
              )}

            <div className="mb-3 font-sans text-sm text-[14px] text-brand-black75">
              {getNearText(prescription)}
            </div>

            <div className="mb-3 font-sans text-sm text-[14px] text-brand-black75">
              {getIntText(prescription)}
            </div>

            {!prescriptionSelected && (
              <div className="font-sans text-sm text-brand-black75">
                {prescription.pd && prescription.pd.binocular_dist_pd && (
                  <p>
                    <span className="font-medium">PD</span>{' '}
                    {prescription.pd.binocular_dist_pd}
                  </p>
                )}
                {prescription.pd &&
                  prescription.pd.right_monocular_dist_pd &&
                  prescription.pd.right_monocular_dist_pd > 0 && (
                    <>
                      <p>
                        <span className="font-medium">PD</span>
                      </p>
                      <p>
                        <span className="font-medium">R</span>{' '}
                        {prescription.pd.right_monocular_dist_pd}mm @dist
                        {prescription.pd.right_height_multifocal && (
                          <span>
                            , Height {prescription.pd.right_height_multifocal}{' '}
                            @centre
                          </span>
                        )}
                      </p>
                      <p>
                        <span className="font-medium">L</span>{' '}
                        {prescription.pd.left_monocular_dist_pd}mm @dist
                        {prescription.pd.left_height_multifocal && (
                          <span>
                            , Height {prescription.pd.left_height_multifocal}{' '}
                            @centre
                          </span>
                        )}
                      </p>
                    </>
                  )}
              </div>
            )}
          </div>
          {prescriptionSelected && (
            <div className="mt-[17px]">
              <PDSelect
                prescriptionId={prescription.id}
                selectedPdId={selectedPdId}
                selectPd={updateSelectedPd}
                pdList={pdOptions}
                onNewPDFormVisiblityChanged={setIsCreatePDFormVisible}
              />
            </div>
          )}
          {prescription && prescription.files[0] && (
            <div className="mt-[8px]">
              <a
                href={
                  prescription.files[prescription.files.length - 1].full_path
                }
                target="_blank"
                className="mb-[8px] mt-[12px] flex items-center text-[14px] font-medium text-brand-black100 hover:text-brand-orange"
                rel="noreferrer">
                <DocumentFile fill="currentColor" />{' '}
                <span className="ml-2">View attached prescription</span>
              </a>
            </div>
          )}
          {prescriptionSelected && !isCreatePDFormVisible && (
            <div className="mt-[12px]">
              <PrescriptionSubmitButton
                loading={isPrescriptionUpdateLoading}
                disabled={!isUsePrescriptionButtonEnabled}
                onClick={submitHandler}>
                Use this prescription
              </PrescriptionSubmitButton>
            </div>
          )}
        </div>
      </div>
      <div ref={dropRef} className="absolute right-[25px] top-[10px] z-20">
        <button
          onClick={() => deleteModal()}
          className="ml-[12px] flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-[2px] bg-white text-brand-black100 transition-colors duration-200 hover:text-brand-orange">
          <TrashOutlined fill="currentColor" />
        </button>
      </div>
    </div>
  );
};

interface PDOption {
  id: string;
  pd?: string;
  rightHeight?: string;
  leftHeight?: string;
  left?: string;
  right?: string;
}

const PDSelect: FC<{
  prescriptionId: number;
  // eslint-disable-next-line no-unused-vars
  selectPd?: (pd: any) => void;
  selectedPdId?: string;
  pdList: PDOption[];
  // eslint-disable-next-line no-unused-vars
  setLoading?: (isLoading: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onNewPDFormVisiblityChanged?: (value: boolean) => void;
}> = ({
  selectPd,
  setLoading,
  onNewPDFormVisiblityChanged,
  prescriptionId,
  selectedPdId,
  pdList
}) => {
    const [isPdsLoading, setIsPdsLoading] = useState(false);

    const [isAddLoading, setIsAddLoading] = useState(false);

    const pds = useSelector((state: RootState) => state.prescriptionEvent.pds);

    const frameSize = useSelector(
      (state: RootState) => state.prescriptionEvent.frameSize
    );

    const armSize = useSelector(
      (state: RootState) => state.prescriptionEvent.armSize
    );

    const dispatch = useDispatch();

    function select(pd: any) {
      selectPd?.(pd);
      dispatch(setUsePrescriptionButton(true));
    }

    const selectedPd = useMemo(() => {
      if (!selectedPdId) return;

      return pdList.find((pd: any) => pd.id === selectedPdId);
    }, [pdList, selectedPdId]);

    const [mutatePdFunction] = useMutation(CREATE_PD, {
      context: { clientName: 'laravel' },
      onCompleted: async (getUserData) => {
        const Newpds = Object.assign([], pds);
        Newpds.unshift(getUserData.createPd);
        dispatch(setPds(Newpds));
        select(getUserData.createPd);
        setIsAddLoading(false);
      },
      onError: () => {
        setIsAddLoading(false);
      }
    });

    const dropdownSelectHandler = (pd: any) => {
      const selectedPd = pds.filter((sPd: any) => sPd !== null).find((sPd: any) => sPd.id === pd.id);

      select(selectedPd);
      setLoading?.(false);
      setIsAddLoading(false);
    };

    const pdAddedHandler: AddPdHandler = (pdType, payload: any) => {
      setIsAddLoading(true);
      mutatePdFunction({
        variables: {
          input: {
            pdType: pdType === 'bino' ? 'single' : 'dual',
            binocular_dist_pd: String(payload?.pd),
            right_monocular_dist_pd: String(payload?.right?.pd),
            left_monocular_dist_pd: String(payload?.left?.pd),
            frame_size: frameSize,
            arm_size: armSize
          }
        }
      });
    };

    return (
      <div
        className={cx({
          hidden: isPdsLoading
        })}>
        <PDSelectDropdownWithAdd
          frameSize={frameSize || ''}
          value={selectedPd as any}
          options={(pdList as any) || []}
          onAdded={pdAddedHandler}
          addLoading={isAddLoading}
          onSelected={dropdownSelectHandler}
          onModeChanged={(mode) => {
            onNewPDFormVisiblityChanged?.(mode === 'add');
          }}
        />
      </div>
    );
  };

type PrescriptionSubmitButtonProps = {
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
};

export const PrescriptionSubmitButton: FC<PrescriptionSubmitButtonProps> = ({
  loading = false,
  disabled = false,
  onClick,
  children
}) => {
  return (
    <button
      onClick={() => {
        if (disabled || loading) return;
        onClick?.();
      }}
      className={cx(
        'flex h-[40px] w-full items-center justify-center rounded-[6px] border px-[30px] text-center text-[14px] font-medium text-white transition-colors duration-200 sm:text-[14px]',
        {
          'border-brand-black100  bg-brand-black100 text-white':
            !loading && !disabled,
          'cursor-pointer hover:text-brand-orange': !disabled && !loading,
          'cursor-not-allowed border-transparent bg-brand-grey75 bg-opacity-50':
            disabled || loading,
          'justify-center': loading
        }
      )}>
      {loading ? (
        <Spinner fill="#fff" />
      ) : (
        <span className="mb-[4px]">{children}</span>
      )}
    </button>
  );
};

export default PrescriptionBox;
